import React, {useCallback, useEffect, useMemo, useState} from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const GET_EXAM_PERIODS_URL = `${API_URL}/Custom/GetExamPeriods`;
const GET_BUILDINGS_URL = `${API_URL}/Custom/GetBuildings`;
const GET_BACKUP_SUMMARY_URL = `${API_URL}/Custom/GetBackupInvigilatorAssignmentSummary`;
const POST_ASSIGN_BUILDING_URL = `${API_URL}/Custom/AssignBuildingStaff`;

export default function BackupInvigilatorAssignmentManager() {
  const [examPeriods, setExamPeriods] = useState([]);
  const [selectedExamPeriod, setSelectedExamPeriod] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [applyToAllBuildings, setApplyToAllBuildings] = useState(true);

  const [summary, setSummary] = useState(null);
  const [examDays, setExamDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedBackupIds, setSelectedBackupIds] = useState([]);
  const [suggestedCount, setSuggestedCount] = useState(1);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [status, setStatus] = useState('');

  const normalizeDateKey = useCallback(value => {
    if (!value) return '';
    if (typeof value === 'string') {
      return value.split('T')[0];
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
  }, []);

  const formatDate = useCallback(dateKey => {
    if (!dateKey) return 'Seçilmedi';
    const date = new Date(`${dateKey}T00:00:00`);
    if (Number.isNaN(date.getTime())) return dateKey;
    return date.toLocaleDateString('tr-TR', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, []);

  const fetchSummary = useCallback(
    async (periodId, day, buildingId) => {
      if (!periodId) {
        setSummary(null);
        setExamDays([]);
        setSelectedBackupIds([]);
        return;
      }

      setIsLoading(true);
      setStatus('');

      try {
        const params = {examPeriodId: periodId};
        if (day) {
          params.examDate = day;
        }
        if (buildingId) {
          params.buildingId = buildingId;
        }

        const response = await axios.get(GET_BACKUP_SUMMARY_URL, {params});
        const data = response.data || null;
        const days = (data?.days || [])
          .map(item => normalizeDateKey(item.date))
          .filter(Boolean);

        setSummary(data);
        setExamDays(days);

        if (!day && days.length > 0) {
          setSelectedDay(days[0]);
        } else if (day && days.length > 0 && !days.includes(day)) {
          setSelectedDay(days[0]);
        }
      } catch (error) {
        const message = error.response?.data?.message || error.response?.data || error.message;
        setStatus(`Hata: ${message}`);
        setSummary(null);
        setExamDays([]);
        setSelectedBackupIds([]);
      } finally {
        setIsLoading(false);
      }
    },
    [normalizeDateKey]
  );

  useEffect(() => {
    axios
      .get(GET_EXAM_PERIODS_URL)
      .then(response => setExamPeriods(response.data || []))
      .catch(error => console.error(error));

    axios
      .get(GET_BUILDINGS_URL)
      .then(response => setBuildings(response.data || []))
      .catch(error => console.error(error));
  }, []);

  useEffect(() => {
    const scopedBuildingId = !applyToAllBuildings && selectedBuilding ? selectedBuilding : null;
    fetchSummary(selectedExamPeriod, selectedDay, scopedBuildingId);
  }, [applyToAllBuildings, fetchSummary, selectedBuilding, selectedExamPeriod, selectedDay]);

  const users = useMemo(() => summary?.users || [], [summary]);
  const days = useMemo(() => summary?.days || [], [summary]);

  const usersById = useMemo(() => {
    const map = new Map();
    users.forEach(user => {
      map.set(Number(user.id), user);
    });
    return map;
  }, [users]);

  const selectedDaySummary = useMemo(
    () => days.find(day => normalizeDateKey(day.date) === selectedDay) || null,
    [days, normalizeDateKey, selectedDay]
  );

  useEffect(() => {
    if (!selectedDaySummary) {
      setSelectedBackupIds([]);
      return;
    }

    const currentBackupIds = (selectedDaySummary.backupUserIds || [])
      .map(Number)
      .filter(Boolean);
    setSelectedBackupIds(currentBackupIds);
  }, [selectedDaySummary]);

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) => {
        const aName = `${a.title || ''} ${a.name || ''}`;
        const bName = `${b.title || ''} ${b.name || ''}`;
        return aName.localeCompare(bName, 'tr');
      }),
    [users]
  );

  const eligibleUsers = useMemo(
    () =>
      sortedUsers
        .filter(user => !user.isBusyOnSelectedDate && !user.isNotAvailableOnSelectedDate)
        .sort((a, b) => {
          if ((a.backupDayCount || 0) !== (b.backupDayCount || 0)) {
            return (a.backupDayCount || 0) - (b.backupDayCount || 0);
          }
          if ((a.backupAssignmentCount || 0) !== (b.backupAssignmentCount || 0)) {
            return (a.backupAssignmentCount || 0) - (b.backupAssignmentCount || 0);
          }
          if ((a.regularAssignmentCount || 0) !== (b.regularAssignmentCount || 0)) {
            return (a.regularAssignmentCount || 0) - (b.regularAssignmentCount || 0);
          }
          return (a.name || '').localeCompare(b.name || '', 'tr');
        }),
    [sortedUsers]
  );

  const selectedNames = useMemo(
    () =>
      selectedBackupIds.map(id => {
        const user = usersById.get(Number(id));
        if (!user) return `#${id}`;
        return `${user.title ? `${user.title} ` : ''}${user.name}`;
      }),
    [selectedBackupIds, usersById]
  );

  const toggleBackup = userId => {
    const id = Number(userId);
    setSelectedBackupIds(previous =>
      previous.includes(id) ? previous.filter(item => item !== id) : [...previous, id]
    );
    setStatus('');
  };

  const handleSuggest = () => {
    const count = Math.max(1, Number(suggestedCount) || 1);
    setSelectedBackupIds(eligibleUsers.slice(0, count).map(user => Number(user.id)));
    setStatus('');
  };

  const handlePeriodChange = event => {
    setSelectedExamPeriod(event.target.value ? Number(event.target.value) : null);
    setSelectedDay('');
    setSelectedBackupIds([]);
    setStatus('');
  };

  const buildPayload = backupIds => ({
    examPeriodId: selectedExamPeriod,
    buildingId: applyToAllBuildings ? 0 : selectedBuilding || 0,
    supervisorIds: [],
    backupInvigilatorIds: backupIds,
    examDate: selectedDay,
    applyBackupToAllBuildings: applyToAllBuildings
  });

  const canUseSelectedScope = applyToAllBuildings || Boolean(selectedBuilding);
  const canSubmit = Boolean(
    selectedExamPeriod &&
      selectedDay &&
      selectedBackupIds.length > 0 &&
      canUseSelectedScope &&
      summary?.hasBackupRole &&
      !isSaving &&
      !isLoading
  );
  const canClear = Boolean(
    selectedExamPeriod &&
      selectedDay &&
      canUseSelectedScope &&
      summary?.hasBackupRole &&
      !isSaving &&
      !isLoading
  );

  const handleSubmit = async event => {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSaving(true);
    setIsClearing(false);
    setStatus('Yedek gözetmenler kaydediliyor...');

    try {
      await axios.post(POST_ASSIGN_BUILDING_URL, buildPayload(selectedBackupIds));
      setStatus('Yedek gözetmen ataması güncellendi.');
      await fetchSummary(selectedExamPeriod, selectedDay, !applyToAllBuildings && selectedBuilding ? selectedBuilding : null);
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data || error.message;
      setStatus(`Hata: ${message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = async () => {
    if (!canClear) return;

    const confirmed = window.confirm('Seçili gün için yedek gözetmen atamaları kaldırılacak. Devam etmek istiyor musunuz?');
    if (!confirmed) return;

    setIsSaving(true);
    setIsClearing(true);
    setStatus('Yedek gözetmenler kaldırılıyor...');

    try {
      await axios.post(POST_ASSIGN_BUILDING_URL, buildPayload([]));
      setSelectedBackupIds([]);
      setStatus('Yedek gözetmen atamaları kaldırıldı.');
      await fetchSummary(selectedExamPeriod, selectedDay, !applyToAllBuildings && selectedBuilding ? selectedBuilding : null);
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data || error.message;
      setStatus(`Hata: ${message}`);
    } finally {
      setIsSaving(false);
      setIsClearing(false);
    }
  };

  const renderUserStatus = user => {
    if (user.isBusyOnSelectedDate) return <span className="badge badge-light-danger">Görevli</span>;
    if (user.isNotAvailableOnSelectedDate) return <span className="badge badge-light-warning">Müsait değil</span>;
    if ((user.selectedDateBackupAssignmentCount || 0) > 0) return <span className="badge badge-light-primary">Yedek</span>;
    return <span className="badge badge-light-success">Uygun</span>;
  };

  return (
    <div className="card p-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4" style={{gap: '1rem'}}>
        <div>
          <h2 className="mb-1">Yedek Gözetmen Ataması</h2>
          <div className="text-muted">Dönem içi yedek gün sayılarına göre dengeli seçim yapılır.</div>
        </div>
        <div className="text-end">
          <span className={`badge ${summary?.hasBackupRole ? 'badge-light-success' : 'badge-light-danger'}`}>
            {summary?.hasBackupRole ? summary.backupRoleName || 'Yedek rolü hazır' : 'Yedek rolü bulunamadı'}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label">Sınav Dönemi</label>
            <select className="form-select" value={selectedExamPeriod || ''} onChange={handlePeriodChange}>
              <option value="" disabled>
                Dönem seçiniz
              </option>
              {examPeriods.map(period => (
                <option key={period.id} value={period.id}>
                  {period.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Sınav Günü</label>
            <select
              className="form-select"
              value={selectedDay}
              onChange={event => setSelectedDay(event.target.value)}
              disabled={examDays.length === 0}
            >
              <option value="" disabled>
                Gün seçiniz
              </option>
              {examDays.map(day => (
                <option key={day} value={day}>
                  {formatDate(day)}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Kapsam</label>
            <select
              className="form-select"
              value={applyToAllBuildings ? 'all' : 'building'}
              onChange={event => {
                const isAll = event.target.value === 'all';
                setApplyToAllBuildings(isAll);
                if (isAll) setSelectedBuilding(null);
              }}
            >
              <option value="all">Tüm binalar</option>
              <option value="building">Seçili bina</option>
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Bina</label>
            <select
              className="form-select"
              value={selectedBuilding || ''}
              onChange={event => setSelectedBuilding(event.target.value ? Number(event.target.value) : null)}
              disabled={applyToAllBuildings}
            >
              <option value="" disabled>
                Bina seçiniz
              </option>
              {buildings.map(building => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {summary && !summary.hasBackupRole && (
          <div className="alert alert-warning mt-4">
            Yedek gözetmen rolü tanımlı değil. Rol adı "Yedek" içermeli ve bina bazlı rol olarak işaretlenmelidir.
          </div>
        )}

        <div className="row g-3 mt-2">
          <div className="col-md-3">
            <div className="border rounded p-3 h-100">
              <div className="text-muted small">Seçili Gün</div>
              <div className="fw-bold">{formatDate(selectedDay)}</div>
              <div className="mt-2 small">Oturum: {selectedDaySummary?.slotCount || 0}</div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="border rounded p-3 h-100">
              <div className="text-muted small">Mevcut Yedek</div>
              <div className="fw-bold">{selectedDaySummary?.backupUserIds?.length || 0} kişi</div>
              <div className="mt-2 small">Kayıt: {selectedDaySummary?.backupAssignmentCount || 0}</div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="border rounded p-3 h-100">
              <div className="text-muted small">Seçilen</div>
              <div className="fw-bold">{selectedBackupIds.length} kişi</div>
              <div className="mt-2 small">{applyToAllBuildings ? 'Tüm binalar' : 'Seçili bina'}</div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="border rounded p-3 h-100">
              <label className="form-label small mb-1">Önerilecek kişi sayısı</label>
              <div className="input-group">
                <input
                  type="number"
                  min="1"
                  className="form-control"
                  value={suggestedCount}
                  onChange={event => setSuggestedCount(event.target.value)}
                />
                <button type="button" className="btn btn-outline-primary" onClick={handleSuggest} disabled={!selectedDay || eligibleUsers.length === 0}>
                  Öner
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 d-flex flex-wrap align-items-center" style={{gap: '0.5rem'}}>
          <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
            {isSaving && !isClearing ? 'Kaydediliyor...' : 'Yedekleri Ata'}
          </button>
          <button type="button" className="btn btn-outline-danger" onClick={handleClear} disabled={!canClear}>
            {isSaving && isClearing ? 'Siliniyor...' : 'Yedekleri Sil'}
          </button>
          <button type="button" className="btn btn-light" onClick={() => setSelectedBackupIds([])} disabled={selectedBackupIds.length === 0}>
            Seçimleri Temizle
          </button>
          {isLoading && <span className="text-muted">Yükleniyor...</span>}
          {status && <span>{status}</span>}
        </div>

        {selectedNames.length > 0 && (
          <div className="alert alert-info mt-4">
            <strong>Seçili yedekler:</strong> {selectedNames.join(', ')}
          </div>
        )}

        <div className="table-responsive mt-4">
          <table className="table table-row-dashed align-middle">
            <thead>
              <tr>
                <th style={{width: 60}}></th>
                <th>Öğretim Elemanı</th>
                <th>Unvan</th>
                <th className="text-center">Yedek Gün</th>
                <th className="text-center">Yedek Kayıt</th>
                <th className="text-center">Normal Görev</th>
                <th className="text-center">Seçili Gün</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map(user => {
                const id = Number(user.id);
                const blocked = user.isBusyOnSelectedDate || user.isNotAvailableOnSelectedDate;
                return (
                  <tr key={id} className={selectedBackupIds.includes(id) ? 'table-primary' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedBackupIds.includes(id)}
                        disabled={blocked}
                        onChange={() => toggleBackup(id)}
                      />
                    </td>
                    <td className="fw-semibold">{user.name}</td>
                    <td>{user.title || '-'}</td>
                    <td className="text-center">{user.backupDayCount || 0}</td>
                    <td className="text-center">{user.backupAssignmentCount || 0}</td>
                    <td className="text-center">{user.regularAssignmentCount || 0}</td>
                    <td className="text-center">{renderUserStatus(user)}</td>
                  </tr>
                );
              })}
              {sortedUsers.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-5">
                    Kayıt bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {days.length > 0 && (
          <div className="table-responsive mt-4">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Gün</th>
                  <th className="text-center">Oturum</th>
                  <th className="text-center">Yedek Kişi</th>
                  <th className="text-center">Yedek Kayıt</th>
                  <th>Yedekler</th>
                </tr>
              </thead>
              <tbody>
                {days.map(day => {
                  const dayKey = normalizeDateKey(day.date);
                  const names = (day.backupUserIds || []).map(id => {
                    const user = usersById.get(Number(id));
                    return user ? user.name : `#${id}`;
                  });

                  return (
                    <tr key={dayKey} className={dayKey === selectedDay ? 'table-light' : ''}>
                      <td>{formatDate(dayKey)}</td>
                      <td className="text-center">{day.slotCount || 0}</td>
                      <td className="text-center">{day.backupUserIds?.length || 0}</td>
                      <td className="text-center">{day.backupAssignmentCount || 0}</td>
                      <td>{names.length > 0 ? names.join(', ') : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </form>
    </div>
  );
}
